import Players from '@/services/player/Players';
import PresetCommunityCards from '@/services/PresetCommunityCards';
import PresetHoleCards from '@/services/PresetHoleCards';
import setBettingCriteria from '@/services/simulator/breakeven/set-betting-criteria-for-calling';
import BettingCriteriaWorker from 'worker-loader!./betting-criteria.worker.js';
import shareUrlService from '@/services/share-url-service';
import storeService from '@/store/service';

let state = {
  presetHoleCards: new PresetHoleCards(),
  presetCommunityCards: new PresetCommunityCards(),
  players: new Players(),
  streetToCall: 'flop',
  potOdds: '2:1',
  realOddsLHS: 2,
  bettingCriteria: {
    flop: {},
    turn: {},
    river: {},
  },
  bettingCriteriaOnePercentIncrements: 0,
};

let getters = {
  breakevenPercentageViaPotOdds: function (state) {
    let split = state.potOdds.split(':');
    let reward = Number(split[0]);
    let risk = Number(split[1]);

    return ((risk / (risk + reward)) * 100).roundToTwoDecimalPlaces();
  },
  breakevenPercentageViaRealOdds: function (state) {
    return ((1 / (1 + Number(state.realOddsLHS))) * 100).roundToTwoDecimalPlaces();
  },
};

let mutations = {
  setState: function (state, stateFromServer) {
    shareUrlService.setGuidedAnalysesCallingStoreState(stateFromServer, state);
  },

  // calling
  setStreetToCall: function (state, streetToCall) {
    state.streetToCall = streetToCall;
  },
  setPotOdds: function (state, potOdds) {
    state.potOdds = potOdds;
  },
  setRealOddsLHS: function (state, realOddsLHS) {
    state.realOddsLHS = realOddsLHS;
  },

  // player range
  selectGeneralHand: function (state, payload) {
    storeService.selectGeneralHand(state, payload);
  },
  deselectGeneralHand: function (state, payload) {
    storeService.deselectGeneralHand(state, payload);
  },
  manuallySelectGeneralHand: function (state, payload) {
    storeService.manuallySelectGeneralHand(state, payload);
  },
  manuallyDeselectGeneralHand: function (state, payload) {
    storeService.manuallyDeselectGeneralHand(state, payload);
  },
  selectGeneralHands: function (state, payload) {
    storeService.selectGeneralHands(state, payload);
  },
  deselectGeneralHands: function (state, payload) {
    storeService.deselectGeneralHands(state, payload);
  },
  deselectAllGeneralHands: function (state, payload) {
    storeService.deselectAllGeneralHands(state, payload);
  },
  selectSpecificHand: function (state, payload) {
    storeService.selectSpecificHand(state, payload);
  },
  deselectSpecificHand: function (state, payload) {
    storeService.deselectSpecificHand(state, payload);
  },
  manuallySelectSpecificHand: function (state, payload) {
    storeService.manuallySelectSpecificHand(state, payload);
  },
  manuallyDeselectSpecificHand: function (state, payload) {
    storeService.manuallyDeselectSpecificHand(state, payload);
  },
  manuallyDeselectAllGeneralHands: function (state, payload) {
    storeService.manuallyDeselectAllGeneralHands(state, payload);
  },
  selectSpecificHands: function (state, payload) {
    storeService.selectSpecificHands(state, payload);
  },
  setSpecificHandAsManuallySet: function (state, payload) {
    storeService.setSpecificHandAsManuallySet(state, payload);
  },
  setSpecificHandAsNotManuallySet: function (state, payload) {
    storeService.setSpecificHandAsManuallySet(state, payload);
  },
  applyExactWeightToGeneralHand: function (state, payload) {
    storeService.applyExactWeightToGeneralHand(state, payload);
  },
  applyExactWeightToGeneralHands: function (state, payload) {
    storeService.applyExactWeightToGeneralHands(state, payload);
  },
  setWeightOfSpecificHand: function (state, payload) {
    storeService.setWeightOfSpecificHand(state, payload);
  },
  manuallyApplyExactWeightToGeneralHand: function (state, payload) {
    storeService.manuallyApplyExactWeightToGeneralHand(state, payload);
  },
  manuallySetWeightOfSpecificHand: function (state, payload) {
    storeService.manuallySetWeightOfSpecificHand(state, payload);
  },
  clearManuallyAddedHands: function (state, payload) {
    storeService.clearManuallyAddedHands(state, payload);
  },
  clearManuallyRemovedHands: function (state, payload) {
    storeService.clearManuallyRemovedHands(state, payload);
  },
  setRangesAccordingToFilters: function (state, playerIndex) {
    storeService.setRangesAccordingToFilters(state, playerIndex);
  },

  // player other
  setWeightToApplyToAddedHands: function (state, payload) {
    storeService.setWeightToApplyToAddedHands(state, payload);
  },
  addComboToCombosToAdd: function (state, payload) {
    storeService.addComboToCombosToAdd(state, payload);
  },
  removeComboFromCombosToAdd: function (state, payload) {
    storeService.removeComboFromCombosToAdd(state, payload);
  },
  highlightGeneralHandsThatMatchFiveCardHand: function (state, payload) {
    storeService.highlightGeneralHandsThatMatchFiveCardHand(state, payload);
  },
  removeFiveCardHandHighlights: function (state, payload) {
    storeService.removeFiveCardHandHighlights(state, payload);
  },

  // controls
  setPresetHoleCard: function (state, payload) {
    storeService.setPresetHoleCard(state, payload);
  },
  removePresetHoleCard: function (state, position) {
    storeService.removePresetHoleCard(state, position);
  },
  setIndividualPresetCommunityCard: function (state, payload) {
    storeService.setIndividualPresetCommunityCard(state, payload);
  },
  setBoard: function (state, boardCards) {
    storeService.setBoard(state, boardCards);
  },
  removePresetCommunityCard: function (state, position) {
    storeService.removePresetCommunityCard(state, position);
  },
  addPlayer: function (state, payload) {
    storeService.addPlayer(state, payload);
  },
  removePlayer: function (state, playerIndex) {
    storeService.removePlayer(state, playerIndex);
  },

  // five card hands section checkboxes
  setCheckedValueOfFiveCardHand: function (state, payload) {
    storeService.setCheckedValueOfFiveCardHand(state, {
      fiveCardHand: state.players.find(payload.playerIndex).states[payload.playerStateIndex].fiveCardHands.find(payload.fiveCardHandId),
      newCheckedValue: payload.newCheckedValue,
    });
    storeService.clearManuallySetHandsThatPassThroughFilter(state, {
      playerIndex: payload.playerIndex,
      playerStateIndex: payload.playerStateIndex,
      fiveCardHandId: payload.fiveCardHandId,
    });
  },
  applyExactWeightToFiveCardHand: function (state, payload) {
    storeService.applyExactWeightToFiveCardHand(state, payload);
  },
  uncheckAllFiveCardHandsForGivenState: function (state, payload) {
    storeService.uncheckAllFiveCardHandsForGivenState(state, payload);
    storeService.clearManuallyAddedHands(state, payload);
    storeService.clearManuallyRemovedHands(state, payload);
  },
  checkAllFiveCardHandsForGivenState: function (state, payload) {
    storeService.checkAllFiveCardHandsForGivenState(state, payload);

    if (state.presetCommunityCards.areAllCardsSet(payload.playerStateIndex)) {
      storeService.clearManuallyAddedHands(state, payload);
      storeService.clearManuallyRemovedHands(state, payload);
    }
  },
  setChecksToDefaultsForGivenState: function (state, payload) {
    storeService.setChecksToDefaultsForGivenState(state, payload);
    storeService.clearManuallyAddedHands(state, payload);
    storeService.clearManuallyRemovedHands(state, payload);
  },
  setChecksAccordingToUserDefinedFilterForGivenState: function (state, payload) {
    storeService.setChecksAccordingToUserDefinedFilterForGivenState(state, payload);
    storeService.clearManuallyAddedHands(state, payload);
    storeService.clearManuallyRemovedHands(state, payload);
  },
  setFiveCardHandValuesToDefaults: function (state, payload) {
    storeService.setFiveCardHandValuesToDefaults(state, payload);
  },
  updateWeightOfFiveCardHand: function (state, payload) {
    storeService.updateWeightOfFiveCardHand(state, {
      fiveCardHand: state.players.find(payload.playerIndex).states[payload.playerStateIndex].fiveCardHands.find(payload.fiveCardHandId),
      newWeight: payload.newWeight,
    });
    storeService.clearManuallySetHandsThatPassThroughFilter(state, {
      playerIndex: payload.playerIndex,
      playerStateIndex: payload.playerStateIndex,
      fiveCardHandId: payload.fiveCardHandId,
    });
  },

  // five card hands section other
  toggleCollapsedValueOfFiveCardHandParent: function (state, fiveCardHandParent) {
    storeService.toggleCollapsedValueOfFiveCardHandParent(state, fiveCardHandParent);
  },
  setProbabilitiesOfFiveCardHands: function (state, payload) {
    storeService.setProbabilitiesOfFiveCardHands(state, payload);
  },
  setHandAndDrawInfo: function (state, payload) {
    storeService.setHandAndDrawInfo(state, payload);
  },
  setPercentagesOfSpecificHandsThatAreSelected: function (state, payload) {
    storeService.setPercentagesOfSpecificHandsThatAreSelected(state, payload);
  },

  // simulations
  setLastSimulationInfo: function (state, playerStateIndex) {
    storeService.setLastSimulationInfo(state, playerStateIndex);
  },
  updateEquityResults: function (state, payload) {
    let equity;
    let wins;
    let ties;
    let winFractions;
    let numberOfSimulations = payload.numberOfSimulations;
    let numPlayers = payload.simulationResults.length;

    state.players.forEach(function (player, playerIndex) {
      wins = payload.simulationResults[playerIndex + 1].wins;
      ties = payload.simulationResults[playerIndex + 1].ties;
      winFractions = payload.simulationResults[playerIndex + 1].winFractions; // equity that comes from ties; ex. with 3 players, when it's a three way tie you'll get 1/3 win fractions, and when it's a two way tie you'll get 1/2 win fractions

      equity = ((wins + winFractions) / numberOfSimulations) * 100;
      equity -= (equity * (payload.rakePercentage / 100)) + (equity * (payload.tipPercentage / 100));
      player.states[0].equity = equity.roundToTwoDecimalPlaces();
      player.states[0].winPercentage = (wins / numberOfSimulations) * 100;
      player.states[0].tiePercentage = (ties / numberOfSimulations) * 100;
    });
  },
};

let actions = {
  setBettingCriteria: function (context, payload) {
    let worker = new BettingCriteriaWorker();
    let totalNumOnePercentIncrements = setBettingCriteria.getTotalNumOnePercentIncrements(context.state.players.find(0), context.state.streetToCall);

    if (totalNumOnePercentIncrements === 0) {
      alert(`Your opponent never has any value hands entering the ${context.state.streetToCall}.`);
      payload.resetSimulationProgress();
    }

    setBettingCriteria.initializeBettingCriteria(context.state.bettingCriteria, context.state.streetToCall);
    setBettingCriteria.setWeightedRangeForEachBettingCriterion(context.state);
    worker.postMessage({
      message: 'set equity for each betting criterion',
      bettingCriteria: context.state.bettingCriteria,
      totalNumOnePercentIncrements: totalNumOnePercentIncrements,
      streetToCall: context.state.streetToCall,
      presetCommunityCardsArray: context.state.presetCommunityCards.getArrayForStateViaStreet(context.state.streetToCall),
      heroWeightedRange: [
        [ context.state.presetHoleCards.holeCardOne, context.state.presetHoleCards.holeCardTwo ]
      ],
      numberOfSimulations: payload.numberOfSimulations,
      rakePercentage: payload.rakePercentage,
      tipPercentage: payload.tipPercentage,
    });

    worker.onmessage = function (e) {
      if (e.data.message === 'progress') {
        context.state.bettingCriteria = e.data.bettingCriteria;
        payload.incrementSimulationProgress(e.data.value);
      }

      else if (e.data.message === 'finished') {
        context.state.bettingCriteria = e.data.bettingCriteria;
        payload.resetSimulationProgress();
      }
    };
  },
};

export default {
  namespaced: true,
  state: state,
  getters: getters,
  mutations: mutations,
  actions: actions,
};



// WEBPACK FOOTER //
// ./src/store/modules/guided-analyses/calling.js